import { useState, useRef } from "react";
import { Camera, Upload, Spinner, Star, Image as ImageIcon } from "@phosphor-icons/react";
import { imageAPI } from "@/lib/api";
import { processImageFile } from "@/lib/image-utils";

export default function PhotoUpload({ verdictId, photos = [], onPhotosChange }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState("");
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const fileList = Array.from(files);
    const base64Images = [];

    for (let i = 0; i < fileList.length; i++) {
      setProgress(`Processing ${i + 1}/${fileList.length}...`);
      try {
        const result = await processImageFile(fileList[i]);
        if (result?.base64 && result.base64.length > 100) {
          base64Images.push(result.base64);
        }
      } catch (err) {
        console.error(`Photo processing failed for ${fileList[i].name}:`, err);
      }
    }

    if (base64Images.length > 0) {
      setProgress(`Uploading ${base64Images.length} photo(s)...`);
      try {
        // Upload one at a time to avoid payload size limits
        const allPaths = [];
        for (let i = 0; i < base64Images.length; i++) {
          setProgress(`Uploading ${i + 1}/${base64Images.length}...`);
          const { data } = await imageAPI.upload([base64Images[i]], verdictId);
          if (data.uploaded > 0) {
            allPaths.push(...data.photos.map((p) => p.path));
          }
        }
        if (allPaths.length > 0 && onPhotosChange) {
          onPhotosChange([...photos, ...allPaths]);
        }
      } catch (err) {
        console.error("Photo upload failed:", err);
      }
    } else {
      console.warn("No valid images to upload");
    }
    setUploading(false);
    setProgress("");
  };

  const setMainPhoto = (index) => {
    if (index === 0 || !onPhotosChange) return;
    const reordered = [...photos];
    const [moved] = reordered.splice(index, 1);
    reordered.unshift(moved);
    onPhotosChange(reordered);
  };

  return (
    <div className="space-y-2" data-testid="photo-upload">
      {/* Photo grid with main indicator + delete */}
      {photos.length > 0 && (
        <div className="space-y-2">
          <div className="flex gap-2 overflow-x-auto py-1">
            {photos.map((path, i) => (
              <div key={path} className="relative shrink-0">
                <button
                  onClick={() => setMainPhoto(i)}
                  title={i === 0 ? "Main photo" : "Tap to set as main photo"}
                  className={`w-20 h-20 bg-neutral-100 overflow-hidden border-2 ${
                    i === 0 ? "border-[#002FA7]" : "border-black"
                  }`}
                >
                  <img
                    src={`/api/images/file/${path}`}
                    alt={`Photo ${i + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  {i === 0 && (
                    <div className="absolute top-0 left-0 bg-[#002FA7] px-1 py-0.5">
                      <Star size={10} weight="fill" className="text-white" />
                    </div>
                  )}
                </button>
                <button
                  onClick={() => {
                    const filtered = photos.filter((_, j) => j !== i);
                    onPhotosChange(filtered);
                  }}
                  className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center"
                  title="Remove"
                >
                  <span className="text-xs font-bold leading-none">&times;</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload controls */}
      {uploading ? (
        <div className="flex items-center gap-2 p-2 bg-neutral-50 border-2 border-neutral-200">
          <Spinner size={16} weight="bold" className="animate-spin" />
          <span className="font-body text-xs">{progress}</span>
        </div>
      ) : (
        <div className="flex gap-2">
          <button
            onClick={() => cameraRef.current?.click()}
            className="flex-1 p-2 bg-black text-white font-heading font-bold uppercase text-xs border-2 border-black flex items-center justify-center gap-1.5 active:translate-y-[1px] transition-transform"
            data-testid="photo-camera-btn"
          >
            <Camera size={14} weight="bold" />
            {photos.length > 0 ? "More" : "Photo"}
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            className="flex-1 p-2 bg-white text-black font-heading font-bold uppercase text-xs border-2 border-black flex items-center justify-center gap-1.5 active:translate-y-[1px] transition-transform"
            data-testid="photo-upload-btn"
          >
            <Upload size={14} weight="bold" />
            Upload
          </button>
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
          <input
            ref={fileRef}
            type="file"
            accept="image/*,.heic,.heif"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </div>
      )}

      {photos.length > 1 && (
        <p className="font-body text-xs text-neutral-500">
          <Star size={10} weight="fill" className="inline mr-0.5 text-[#002FA7]" />
          Tap a photo to set it as the main listing image
        </p>
      )}
      {photos.length === 1 && (
        <p className="font-body text-xs text-neutral-500">
          <ImageIcon size={12} weight="bold" className="inline mr-1" />
          1 photo attached (main image)
        </p>
      )}
    </div>
  );
}
